import CopyButton from '../../components/quick/CopyButton';
import { BsWhatsapp } from 'react-icons/bs';
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../../utils/api';
import { BsThreeDotsVertical } from 'react-icons/bs';
import './ClientsTable.css';

const pageSizes = [10, 25, 50];

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [sort, setSort] = useState({ field: 'createdAt', order: 'desc' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [syncStatus, setSyncStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

  const token = localStorage.getItem('token');
  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page,
        pageSize,
        search,
        ...(tipoFilter ? { tipo: tipoFilter } : {}),
        ...(estadoFilter ? { estado: estadoFilter } : {}),
        ...(duplicatesOnly ? { duplicates: 'true' } : {}),
        sort: sort.field,
        order: sort.order
      });
      const res = await apiFetch(`/api/clients?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando clientes');
      setClients(Array.isArray(data.data) ? data.data : []);
      setTotal(Number.isFinite(data.total) ? data.total : 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sort, search, tipoFilter, estadoFilter, duplicatesOnly]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const handleSort = (field) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { field, order: 'asc' };
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cliente?')) return;
    try {
      const res = await apiFetch(`/api/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      fetchClients();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSyncDolibarr = async () => {
    setSyncStatus({ type: 'info', message: 'Sincronizando...' });
    try {
      const res = await apiFetch('/api/integrations/dolibarr/sync-clients', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo sincronizar');
      setSyncStatus({ type: 'success', message: `${data.message}. Importados: ${data.imported}` });
      fetchClients();
    } catch (err) {
      setSyncStatus({ type: 'error', message: err.message });
    }
  };

  const handleImportDolibarrById = async () => {
    const raw = window.prompt('ID Dolibarr a importar');
    if (!raw) return;
    const id = Number(raw);
    if (!Number.isFinite(id)) return alert('ID inválido');
    setSyncStatus({ type: 'info', message: `Importando Dolibarr #${id}...` });
    try {
      const res = await apiFetch('/api/integrations/dolibarr/import-client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo importar');
      setSyncStatus({ type: 'success', message: data.message || 'Cliente importado' });
      fetchClients();
    } catch (err) {
      setSyncStatus({ type: 'error', message: err.message });
    }
  };

  const renderSortIcon = (field) => {
    if (sort.field !== field) return '⇅';
    return sort.order === 'asc' ? '↑' : '↓';
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h2 className="mb-0">Clientes/Leads</h2>
          <div className="btn-group">
            <Link className="btn btn-sm btn-primary" to="/clientes">Tabla Clientes</Link>
            <Link className="btn btn-sm btn-outline-primary" to="/clientes/leads">Leads</Link>
          </div>
        </div>
        {isMobile ? (
          <div className="position-relative">
            <button className="btn btn-outline-secondary" onClick={() => setActionsMenuOpen((o) => !o)}>
              <BsThreeDotsVertical />
            </button>
            {actionsMenuOpen && (
              <div className="dropdown-menu dropdown-menu-end show" style={{ right: 0, left: 'auto', minWidth: '200px' }}>
                <button className="dropdown-item" onClick={() => { handleSyncDolibarr(); setActionsMenuOpen(false); }}>
                  Sincronizar clientes Dolibarr
                </button>
                <button className="dropdown-item" onClick={() => { handleImportDolibarrById(); setActionsMenuOpen(false); }}>
                  Importar por ID Dolibarr
                </button>
                <Link className="dropdown-item" to="/clientes/nuevo" onClick={() => setActionsMenuOpen(false)}>
                  Crear nuevo cliente
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={handleSyncDolibarr}>
              Sincronizar clientes Dolibarr
            </button>
            <button className="btn btn-outline-secondary" onClick={handleImportDolibarrById}>
              Importar por ID Dolibarr
            </button>
            <Link className="btn btn-primary" to="/clientes/nuevo">
              Crear nuevo cliente
            </Link>
          </div>
        )}
      </div>
      {syncStatus && (
        <div className={`alert alert-${syncStatus.type === 'success' ? 'success' : syncStatus.type === 'error' ? 'danger' : 'info'}`}>
          {syncStatus.message}
        </div>
      )}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-2 mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre, email, ciudad o teléfono"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              style={{ maxWidth: 320 }}
            />
            <select
              className="form-select"
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
              style={{ width: 120 }}
            >
              {pageSizes.map((s) => (
                <option key={s} value={s}>{s} por página</option>
              ))}
            </select>
            <select
              className="form-select"
              value={tipoFilter}
              onChange={(e) => {
                setPage(1);
                setTipoFilter(e.target.value);
              }}
              style={{ width: 160 }}
            >
              <option value="">Todos</option>
              <option value="Cliente">Clientes</option>
              <option value="Lead">Leads</option>
            </select>
            <select
              className="form-select"
              aria-label="Filtrar por estado"
              value={estadoFilter}
              onChange={(e) => {
                setPage(1);
                setEstadoFilter(e.target.value);
              }}
              style={{ width: 180 }}
            >
              <option value="">Todos los estados</option>
              <option value="Activo">Activos</option>
              <option value="Inactivo">Inactivos</option>
            </select>
            <button
              className={`btn ${duplicatesOnly ? 'btn-warning' : 'btn-outline-secondary'}`}
              onClick={() => {
                setPage(1);
                setDuplicatesOnly((p) => !p);
              }}
            >
              {duplicatesOnly ? 'Ver todos' : 'Ver duplicados'}
            </button>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <div className="table-responsive">
            <table className="table clients-table">
              <thead className="clients-table__head">
                <tr>
                  <th role="button" onClick={() => handleSort('razonSocial')} className="clients-table__th clients-table__th--sortable clients-table__col-razon">
                    Razón Social <span className="clients-table__sort">{renderSortIcon('razonSocial')}</span>
                  </th>
                  <th className="clients-table__th clients-table__col-cif">CIF/NIF</th>
                  <th className="clients-table__th clients-table__col-tipo">Tipo</th>
                  <th className="clients-table__th clients-table__col-estado">Estado</th>
                  <th role="button" onClick={() => handleSort('ciudad')} className="clients-table__th clients-table__th--sortable clients-table__col-loc">
                    Ciudad <span className="clients-table__sort">{renderSortIcon('ciudad')}</span>
                  </th>
                  <th role="button" onClick={() => handleSort('provincia')} className="clients-table__th clients-table__th--sortable clients-table__col-loc">
                    Provincia <span className="clients-table__sort">{renderSortIcon('provincia')}</span>
                  </th>
                  <th role="button" onClick={() => handleSort('pais')} className="clients-table__th clients-table__th--sortable clients-table__col-loc">
                    País <span className="clients-table__sort">{renderSortIcon('pais')}</span>
                  </th>
                  <th className="clients-table__th clients-table__col-email">Email</th>
                  <th className="clients-table__th clients-table__col-phone">Teléfono</th>
                  <th className="clients-table__th" title="Acceso al área de cliente">Portal</th>
                  <th className="clients-table__th clients-table__col-actions">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="clients-table__msg">
                      <span className="spinner-border spinner-border-sm text-primary me-2" role="status" aria-hidden="true"></span>
                      Cargando...
                    </td>
                  </tr>
                ) : clients.length ? (
                  clients.map((c) => (
                    <tr key={c.id} className={`clients-table__row${c.dup_email || c.dup_cif ? ' clients-table__row--dup' : ''}`}>
                      <td className="clients-table__col-razon">
                        <button
                          type="button"
                          className="clients-table__name"
                          onClick={() => navigate(`/clientes/${c.id}`)}
                          title={c.razon_social}
                        >
                          {c.razon_social}
                        </button>
                      </td>
                      <td className="clients-table__col-cif"><span className="clients-table__mono">{c.cif || '-'}<CopyButton value={c.cif} label="CIF" /></span></td>
                      <td className="clients-table__col-tipo">
                        <span className={`badge bg-${c.tipo === 'Lead' ? 'warning text-dark' : 'success'}`}>{c.tipo || '-'}</span>
                      </td>
                      <td className="clients-table__col-estado">
                        <span className={`badge bg-${c.estado === 'Inactivo' ? 'secondary' : 'info text-dark'}`}>{c.estado || '-'}</span>
                      </td>
                      <td className="clients-table__col-loc"><span className="clients-table__ellipsis" title={c.ciudad || ''}>{c.ciudad || '-'}</span></td>
                      <td className="clients-table__col-loc"><span className="clients-table__ellipsis" title={c.provincia || ''}>{c.provincia || '-'}</span></td>
                      <td className="clients-table__col-loc"><span className="clients-table__ellipsis" title={c.pais || ''}>{c.pais || '-'}</span></td>
                      <td className="clients-table__col-email">
                        <div className="clients-table__copy-value"><span className="clients-table__ellipsis" title={c.email || ''}>{c.email || '-'}</span><CopyButton value={c.email} label="email" /></div>
                        {(c.dup_email || c.dup_cif) && (
                          <span className="clients-table__dup">
                            {c.dup_email ? 'Email duplicado. ' : ''}{c.dup_cif ? 'CIF duplicado.' : ''}
                          </span>
                        )}
                      </td>
                      <td className="clients-table__col-phone"><span className="clients-table__mono">{c.telefono || '-'}</span><CopyButton value={c.telefono} label="teléfono" /></td>
                      <td>
                        {c.portal_access
                          ? <span className="badge bg-success" title="Acceso al portal activado">Activo</span>
                          : <span className="badge bg-light text-dark" title="Sin acceso al portal">No</span>}
                      </td>
                      <td className="clients-table__col-actions">
                        <div className="clients-table__actions">
                          {c.telefono && <Link className="btn btn-sm btn-outline-success" title="Abrir WhatsApp" aria-label={`WhatsApp de ${c.razon_social}`} to={`/whatsapp?phone=${encodeURIComponent(c.telefono)}`}><BsWhatsapp /></Link>}
                          <Link className="btn btn-sm btn-outline-primary" to={`/clientes/${c.id}/editar`} title="Editar cliente">Editar</Link>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(c.id)} title="Eliminar cliente">Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="clients-table__msg clients-table__msg--empty">Sin resultados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div>
              Página {page} de {totalPages} (total {total})
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsPage;
